/**
 * Governance Document Lens - Client Type Definitions
 * ============================================================================
 * DC-1040-1046: Client-side types for Document Lens UI
 *
 * Mirrors server types from server/lenses/governanceDocument/types.ts
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

// ============================================================================
// CORE ENUMS AND TYPES
// ============================================================================

/**
 * Available governance lens identifiers
 */
export type GovernanceLensId =
  | 'fair'
  | 'ml_readiness'
  | 'privacy_compliance'
  | 'regulatory'
  | 'data_quality'
  | 'financial_services';

/**
 * Supported regulation schemas for deep analysis
 */
export type RegulationSchemaId =
  | 'sr11-7'
  | 'gdpr'
  | 'fda-ai-ml'
  | 'eu-ai-act'
  | 'hipaa'
  | 'ccpa';

/**
 * Finding severity levels
 */
export type FindingSeverity = 'critical' | 'major' | 'minor' | 'observation';

/**
 * Compliance level for deep analysis
 */
export type ComplianceLevel = 'compliant' | 'partial' | 'non_compliant' | 'not_applicable';

/**
 * Effort level for remediation actions
 */
export type EffortLevel = 'low' | 'medium' | 'high';

/**
 * Document category for lens selection hints
 */
export type DocumentCategory =
  | 'data_access'
  | 'data_quality'
  | 'data_lifecycle'
  | 'metadata'
  | 'privacy'
  | 'security'
  | 'retention'
  | 'ai_governance'
  | 'model_risk'
  | 'group_transcript'
  | 'other';

// ============================================================================
// LENS METADATA
// ============================================================================

/**
 * Lens display information
 */
export interface LensInfo {
  lens_id: GovernanceLensId;
  lens_name: string;
  description: string;
  icon: string;
  color: string;
  category: string;
}

/**
 * Regulation schema information
 */
export interface RegulationSchemaInfo {
  id: RegulationSchemaId;
  name: string;
  shortName: string;
  description: string;
  jurisdiction: string;
  industry: string;
  version: string;
  effectiveDate: string;
  requirementCount: number;
  isImplemented: boolean;
}

// ============================================================================
// TIER 1: FAST DETECTION TYPES
// ============================================================================

/**
 * Concept detected in document text
 */
export interface DetectedConcept {
  concept_id: string;
  concept_name: string;
  matched_text: string;
  line_number?: number;
  confidence: number;
  category: string;
}

/**
 * Finding from Tier 1 analysis
 */
export interface Tier1Finding {
  id: string;
  lens_id: GovernanceLensId;
  severity: FindingSeverity;
  type: 'missing_concept' | 'weak_coverage' | 'recommendation';
  title: string;
  description: string;
  affected_concept?: string;
  suggestion?: string;
}

/**
 * Result from a single lens Tier 1 check
 */
export interface LensResult {
  lens_id: GovernanceLensId;
  score: number;
  concepts_covered: DetectedConcept[];
  concepts_missing: string[];
  total_concepts: number;
  coverage_percentage: number;
  findings: Tier1Finding[];
  analyzed_at: string;
}

/**
 * Summary of findings by severity
 */
export interface FindingsSummary {
  critical: number;
  major: number;
  minor: number;
  observation: number;
  total: number;
}

/**
 * Aggregated result from Tier 1 multi-lens analysis
 */
export interface GovernanceLensResult {
  document_id: string;
  org_id: string;
  overall_score: number;
  lens_results: Partial<Record<GovernanceLensId, LensResult>>;
  all_findings: Tier1Finding[];
  findings_summary: FindingsSummary;
  weights_used: Record<GovernanceLensId, number>;
  detected_regulations: RegulationSchemaId[];
  analyzed_at: string;
  analysis_version: string;
  initiated_by?: string;
}

// ============================================================================
// TIER 2: DEEP ANALYSIS TYPES
// ============================================================================

/**
 * Evaluation result for a single requirement
 */
export interface RequirementEvaluation {
  requirement_id: string;
  requirement_title: string;
  section?: string;
  status: 'met' | 'partial' | 'not_met' | 'not_applicable';
  score: number;
  evidence: string[];
  gaps: string[];
  remediation?: string;
  confidence: number;
}

/**
 * Remediation action item
 */
export interface RemediationAction {
  priority: number;
  title: string;
  description: string;
  related_requirements: string[];
  effort: EffortLevel;
  expected_impact: 'high' | 'medium' | 'low';
  suggested_owner?: string;
}

/**
 * Complete remediation plan
 */
export interface RemediationPlan {
  summary: string;
  actions: RemediationAction[];
  critical_gaps: string[];
  estimated_effort: EffortLevel;
}

/**
 * Regulatory citation reference
 */
export interface RegulatoryCitation {
  id: string;
  regulation_id: RegulationSchemaId;
  section: string;
  text: string;
  full_reference: string;
  url?: string;
}

/**
 * Result from Tier 2 deep analysis
 */
export interface DeepAnalysisResult {
  id: string;
  document_id: string;
  org_id: string;
  regulation_id: RegulationSchemaId;
  compliance_score: number;
  compliance_level: ComplianceLevel;
  requirement_evaluations: RequirementEvaluation[];
  requirements_met: number;
  requirements_partial: number;
  requirements_not_met: number;
  total_requirements: number;
  remediation_plan: RemediationPlan;
  evidence_found: string[];
  regulatory_citations: RegulatoryCitation[];
  analyzed_at: string;
  analyzed_by?: string;
  analysis_version: string;
  model_used: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

/**
 * Response from GET /governance-documents/lenses
 */
export interface LensesResponse {
  success: boolean;
  result: {
    lenses: LensInfo[];
    count: number;
  };
  error?: string;
}

/**
 * Response from GET /governance-documents/regulations
 */
export interface RegulationsResponse {
  success: boolean;
  result: {
    schemas: RegulationSchemaInfo[];
    implemented: RegulationSchemaId[];
    stats: {
      totalSchemas: number;
      totalRequirements: number;
    };
  };
  error?: string;
}

/**
 * Response from POST /governance-documents/:id/analyze
 */
export interface AnalyzeResponse {
  success: boolean;
  result: GovernanceLensResult;
  meta?: {
    documentName: string;
    category: DocumentCategory;
    lensesRun: GovernanceLensId[];
    findingsCount: number;
  };
  error?: string;
}

/**
 * Response from POST /governance-documents/:id/deep-analyze
 */
export interface DeepAnalyzeResponse {
  success: boolean;
  result: DeepAnalysisResult;
  meta?: {
    documentName: string;
    regulationName: string;
  };
  error?: string;
}

/**
 * Response from GET /governance-documents/:id/tier1-results
 */
export interface Tier1ResultsResponse {
  success: boolean;
  result: {
    document_id: string;
    lens_scores: Record<string, number>;
    findings: Tier1Finding[];
    detected_regulations: RegulationSchemaId[];
    analyzed_at: string;
  };
  meta?: {
    documentName: string;
    category: DocumentCategory;
  };
  error?: string;
}

/**
 * Response from GET /governance-documents/compliance-stats
 */
export interface ComplianceStatsResponse {
  success: boolean;
  result: {
    totalDocuments: number;
    analyzedDocuments: number;
    averageLensScore: number;
    deepAnalysesCount: number;
    complianceByRegulation: Record<RegulationSchemaId, {
      documentsAnalyzed: number;
      averageScore: number;
      complianceLevel: ComplianceLevel;
    }>;
  };
  error?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Lens display configuration
 */
export const LENS_CONFIG: Record<GovernanceLensId, {
  name: string;
  shortName: string;
  icon: string;
  color: string;
  bgColor: string;
}> = {
  fair: {
    name: 'FAIR Compliance',
    shortName: 'FAIR',
    icon: 'F',
    color: '#3B82F6',
    bgColor: '#EFF6FF',
  },
  ml_readiness: {
    name: 'ML Readiness',
    shortName: 'ML',
    icon: 'M',
    color: '#8B5CF6',
    bgColor: '#F5F3FF',
  },
  privacy_compliance: {
    name: 'Privacy Compliance',
    shortName: 'Privacy',
    icon: 'P',
    color: '#10B981',
    bgColor: '#ECFDF5',
  },
  regulatory: {
    name: 'Regulatory Standards',
    shortName: 'Regulatory',
    icon: 'R',
    color: '#F59E0B',
    bgColor: '#FFFBEB',
  },
  data_quality: {
    name: 'Data Quality',
    shortName: 'DQ',
    icon: 'Q',
    color: '#06B6D4',
    bgColor: '#ECFEFF',
  },
  financial_services: {
    name: 'Financial Services',
    shortName: 'FS',
    icon: '$',
    color: '#EF4444',
    bgColor: '#FEF2F2',
  },
};

/**
 * Regulation display configuration
 */
export const REGULATION_CONFIG: Record<RegulationSchemaId, {
  name: string;
  shortName: string;
  jurisdiction: string;
}> = {
  'sr11-7': {
    name: 'Federal Reserve SR 11-7 (Model Risk)',
    shortName: 'SR 11-7',
    jurisdiction: 'US',
  },
  'gdpr': {
    name: 'EU GDPR',
    shortName: 'GDPR',
    jurisdiction: 'EU',
  },
  'fda-ai-ml': {
    name: 'FDA AI/ML 2025 Guidance',
    shortName: 'FDA AI/ML',
    jurisdiction: 'US',
  },
  'eu-ai-act': {
    name: 'EU AI Act',
    shortName: 'EU AI Act',
    jurisdiction: 'EU',
  },
  'hipaa': {
    name: 'HIPAA',
    shortName: 'HIPAA',
    jurisdiction: 'US',
  },
  'ccpa': {
    name: 'CCPA',
    shortName: 'CCPA',
    jurisdiction: 'US',
  },
};

/**
 * Severity configuration
 */
export const SEVERITY_CONFIG: Record<FindingSeverity, {
  label: string;
  color: string;
  bgColor: string;
  icon: string;
}> = {
  critical: {
    label: 'Critical',
    color: '#DC2626',
    bgColor: '#FEF2F2',
    icon: '⛔',
  },
  major: {
    label: 'Major',
    color: '#EA580C',
    bgColor: '#FFF7ED',
    icon: '⚠',
  },
  minor: {
    label: 'Minor',
    color: '#CA8A04',
    bgColor: '#FEFCE8',
    icon: '!',
  },
  observation: {
    label: 'Observation',
    color: '#2563EB',
    bgColor: '#EFF6FF',
    icon: 'ℹ',
  },
};

/**
 * Compliance level configuration
 */
export const COMPLIANCE_LEVEL_CONFIG: Record<ComplianceLevel, {
  label: string;
  color: string;
  bgColor: string;
  description: string;
}> = {
  compliant: {
    label: 'Compliant',
    color: '#16A34A',
    bgColor: '#F0FDF4',
    description: 'Document meets all key requirements. Ready for use.',
  },
  partial: {
    label: 'Partial',
    color: '#CA8A04',
    bgColor: '#FEFCE8',
    description: 'Document covers most requirements with minor gaps.',
  },
  non_compliant: {
    label: 'Non-Compliant',
    color: '#DC2626',
    bgColor: '#FEF2F2',
    description: 'Document has significant gaps requiring remediation.',
  },
  not_applicable: {
    label: 'N/A',
    color: '#6B7280',
    bgColor: '#F3F4F6',
    description: 'This regulation does not apply to this document.',
  },
};

/**
 * Effort level configuration
 */
export const EFFORT_CONFIG: Record<EffortLevel, {
  label: string;
  dots: number;
  description: string;
}> = {
  low: {
    label: 'Low',
    dots: 1,
    description: 'Quick fix, can be done in hours',
  },
  medium: {
    label: 'Medium',
    dots: 2,
    description: 'Moderate effort, requires days',
  },
  high: {
    label: 'High',
    dots: 3,
    description: 'Significant effort, requires weeks',
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get score color based on value
 */
export function getScoreColor(score: number): string {
  if (score >= 90) return '#16A34A'; // Green
  if (score >= 70) return '#CA8A04'; // Yellow
  if (score >= 50) return '#EA580C'; // Orange
  return '#DC2626'; // Red
}

/**
 * Get compliance level from score
 */
export function getComplianceLevel(score: number): ComplianceLevel {
  if (score >= 90) return 'compliant';
  if (score >= 70) return 'partial';
  return 'non_compliant';
}

/**
 * Sort findings by severity (most severe first)
 */
export function sortFindingsBySeverity<T extends { severity: FindingSeverity }>(
  findings: T[]
): T[] {
  const order: Record<FindingSeverity, number> = {
    critical: 0,
    major: 1,
    minor: 2,
    observation: 3,
  };
  return [...findings].sort((a, b) => order[a.severity] - order[b.severity]);
}

/**
 * Summarize findings by severity
 */
export function summarizeFindings(findings: Tier1Finding[]): FindingsSummary {
  const summary: FindingsSummary = {
    critical: 0,
    major: 0,
    minor: 0,
    observation: 0,
    total: findings.length,
  };

  for (const finding of findings) {
    summary[finding.severity]++;
  }

  return summary;
}

/**
 * All available lens IDs
 */
export const ALL_LENS_IDS: GovernanceLensId[] = [
  'fair',
  'ml_readiness',
  'privacy_compliance',
  'regulatory',
  'data_quality',
  'financial_services',
];

/**
 * All available regulation IDs
 */
export const ALL_REGULATION_IDS: RegulationSchemaId[] = [
  'sr11-7',
  'gdpr',
  'fda-ai-ml',
  'eu-ai-act',
  'hipaa',
  'ccpa',
];
